import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { AppStore } from '../../store/applicationState';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { EditFactoids } from './EditFactoids';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Apps from '@mui/icons-material/Apps';
import DoneIcon from '@mui/icons-material/Done';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import { WarningModal } from '../modals';
import { useSearch } from '../../hooks/useSearch';
import {
  DeleteFactoids,
  GetFactoids,
  resetFactoids,
  SearchFactoids,
  setEditData,
  setListData,
} from '../../store/factoids/actions';
import moment from 'moment/moment';
import { ListFactoidBlocks } from './ListFactoidBlocks';

interface Column {
  id: 'id' | 'title' | 'tags' | 'created_at' | 'approved' | 'blocks' | 'edit' | 'delete';
  label: string;
  align?: 'right' | 'left' | 'center';
  width?: string;
  format?: (value: number) => string;
}

const columns: Column[] = [
  { id: 'id', label: '№', align: 'center', width: '6%' },
  { id: 'title', label: 'Заголовок', align: 'left' },
  { id: 'tags', label: 'Теги', align: 'left' },
  { id: 'approved', label: 'Опубліковано', align: 'center' },
  { id: 'created_at', label: 'Дата створення', align: 'center' },
  {
    id: 'blocks',
    label: 'Блоки',
    align: 'center',
    width: '9%',
  },
  {
    id: 'edit',
    label: 'Редагувати',
    align: 'center',
    width: '9%',
  },
  {
    id: 'delete',
    label: 'Видалити',
    align: 'center',
    width: '8%',
  },
];

type PropsType = {};

export const ListFactoids: React.FC<PropsType> = () => {
  const [isModal, setIsModal] = React.useState<{ id: number } | null>(null);
  const { Factoids, Configurations } = useSelector((store: AppStore) => store);
  const dispatch = useDispatch();

  const { loading, searchId, searchTitle, handleSearchId, handleSearchTitle } = useSearch({
    SearchData: SearchFactoids,
    ResetData: resetFactoids,
  });

  React.useEffect(() => {
    if (Configurations.siteId) {
      dispatch(
        GetFactoids.request({
          data: {
            siteId: Configurations.siteId,
          },
        })
      );
    }
  }, [dispatch, Configurations.siteId]);

  const handleRemove = (id: number) => {
    dispatch(DeleteFactoids.request({ id }));
  };

  if (Factoids.editData) {
    return (
      <EditFactoids
        type={Factoids.editData.type}
        data={Factoids.editData.data}
        handleCancel={() => dispatch(setEditData(null))}
      />
    );
  }

  if (Factoids.listBlockData) {
    return (
      <ListFactoidBlocks factoid={Factoids.listBlockData.factoid} handleCancel={() => dispatch(setListData(null))} />
    );
  }

  return (
    <>
      <Box >
        <Button
          
          variant='contained'
          color='primary'
          onClick={() => {
            dispatch(setEditData({ type: 'new' }));
          }}
        >
          Створити
        </Button>
        <TextField
          size='small'
          id='outlined-basic-id'
          label='Пошук по ID'
          variant='outlined'
          value={searchId}
          
          onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
            handleSearchId(event.target.value as string);
          }}
        />
        <TextField
          
          size='small'
          id='outlined-basic-title'
          label='Пошук по заголовку'
          variant='outlined'
          value={searchTitle}
          onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
            handleSearchTitle(event.target.value as string);
          }}
        />
        {loading ? <CircularProgress size={25} /> : null}
      </Box>
      <TableContainer >
        <Table  stickyHeader aria-label='sticky table'>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.id} align={column.align} style={{ width: column?.width || '' }}>
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {Factoids.factoids?.map((row, i) => {
              return (
                <TableRow style={{ maxHeight: 50 }} hover role='checkbox' tabIndex={-1} key={`${i}-${row.id}`}>
                  <TableCell align='center'>{row.id}</TableCell>
                  <TableCell >{row.title}</TableCell>
                  <TableCell >{row.tags}</TableCell>
                  <TableCell align='center' >
                    {row.approved ? <DoneIcon /> : ''}
                  </TableCell>
                  <TableCell  align='center'>
                    {moment(row.created_at).format('DD.MM.YYYY HH:mm')}
                  </TableCell>
                  <TableCell  align='center'>
                    <IconButton aria-label='Блоки' onClick={() => dispatch(setListData({ factoid: row }))}>
                      <Apps />
                    </IconButton>
                  </TableCell>
                  <TableCell  align='center'>
                    <IconButton aria-label='edit' onClick={() => dispatch(setEditData({ type: 'edit', data: row }))}>
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                  <TableCell align='center'>
                    <IconButton aria-label='delete' onClick={() => setIsModal({ id: row.id })}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {isModal ? (
        <WarningModal
          handleApprove={(isApprove) => {
            isApprove && handleRemove(isModal.id);
            setIsModal(null);
          }}
          value={`№ ${isModal.id}`}
          open={!!isModal}
        />
      ) : null}
    </>
  );
};
